/**
 * Utilities for matching Mapbox address results against GAPI property-record responses.
 * GAPI may return one row with parallel arrays (id/folio/address/url/state) for condo parcels.
 */

const PARALLEL_PROPERTY_KEYS = ['id', 'folio', 'address', 'url', 'state'];

const getLeadingAddressNumber = (value) => {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const match = value.trim().match(/^(\d+[a-zA-Z]?)/);
  return match ? match[1].toLowerCase() : null;
};

const getMapboxResultQueries = (result = {}) => {
  const queries = [];
  const push = (s) => {
    if (s && typeof s === 'string' && s.trim()) {
      queries.push(s.trim());
    }
  };

  push(result.place_name);
  push(result.text);
  push(result.matching_place_name);

  const placeName = result.place_name;
  if (placeName && typeof placeName === 'string' && placeName.includes(',')) {
    push(placeName.split(',')[0].trim());
  }

  return [...new Set(queries)];
};

const scoreCandidateAgainstQuery = (candidate, query, compareFn) => {
  if (!candidate || typeof candidate !== 'string' || !query || typeof query !== 'string') {
    return -Infinity;
  }

  let score = compareFn(candidate, query);
  const candidateNumber = getLeadingAddressNumber(candidate);
  const queryNumber = getLeadingAddressNumber(query);

  // Prevent close string matches from beating the exact house number.
  if (candidateNumber && queryNumber) {
    if (candidateNumber === queryNumber) {
      score += 0.25;
    }
    else {
      score -= 1;
    }
  }

  return score;
};

const scoreCandidateAgainstQueries = (candidate, queries, compareFn) => {
  if (!candidate || typeof candidate !== 'string' || !queries.length) {
    return -Infinity;
  }

  let best = -Infinity;
  queries.forEach((query) => {
    const score = scoreCandidateAgainstQuery(candidate, query, compareFn);
    if (score > best) {
      best = score;
    }
  });
  return best;
};

const getParallelVariantCount = (item = {}) => {
  let count = 0;
  PARALLEL_PROPERTY_KEYS.forEach((key) => {
    const value = item[key];
    if (Array.isArray(value) && value.length > count) {
      count = value.length;
    }
  });
  return count;
};

const slicePropertyVariant = (item, index) => {
  const output = {...item};
  PARALLEL_PROPERTY_KEYS.forEach((key) => {
    const value = item[key];
    if (Array.isArray(value) && value.length) {
      const safeIndex = Math.min(index, value.length - 1);
      output[key] = value[safeIndex];
    }
  });

  if (typeof output.address === 'string' && output.address) {
    output.title = output.address;
  }

  return output;
};

const computeRowBestScore = (row, queries, compareFn) => {
  const variantCount = getParallelVariantCount(row);
  if (variantCount <= 1) {
    const candidates = [];
    if (row.title) {
      candidates.push(row.title);
    }
    if (typeof row.address === 'string' && row.address) {
      candidates.push(row.address);
    }
    if (!candidates.length) {
      return -Infinity;
    }
    return Math.max(...candidates.map(candidate => scoreCandidateAgainstQueries(candidate, queries, compareFn)));
  }

  let best = -Infinity;
  for (let i = 0; i < variantCount; i += 1) {
    const addressLine = Array.isArray(row.address) ? row.address[i] : '';
    const score = Math.max(
      scoreCandidateAgainstQueries(addressLine, queries, compareFn),
      scoreCandidateAgainstQueries(row.title || '', queries, compareFn),
    );
    if (score > best) {
      best = score;
    }
  }

  return best;
};

const resolvePropertySearch = (data, result, compareFn) => {
  if (!data?.length) {
    return null;
  }

  const queries = getMapboxResultQueries(result);
  if (!queries.length) {
    const firstItem = data[0];
    return getParallelVariantCount(firstItem) <= 1 ? firstItem : slicePropertyVariant(firstItem, 0);
  }

  let bestRowIndex = 0;
  let bestRowScore = -Infinity;
  data.forEach((row, rowIndex) => {
    const rowScore = computeRowBestScore(row, queries, compareFn);
    if (rowScore > bestRowScore) {
      bestRowScore = rowScore;
      bestRowIndex = rowIndex;
    }
    else if (rowScore === bestRowScore && rowIndex < bestRowIndex) {
      bestRowIndex = rowIndex;
    }
  });

  const selectedRow = data[bestRowIndex];
  const variantCount = getParallelVariantCount(selectedRow);
  if (variantCount <= 1) {
    return selectedRow;
  }

  let bestVariantIndex = 0;
  let bestVariantScore = -Infinity;
  for (let i = 0; i < variantCount; i += 1) {
    const addressLine = Array.isArray(selectedRow.address) ? selectedRow.address[i] : '';
    const score = Math.max(
      scoreCandidateAgainstQueries(addressLine, queries, compareFn),
      scoreCandidateAgainstQueries(selectedRow.title || '', queries, compareFn),
    );
    if (score > bestVariantScore) {
      bestVariantScore = score;
      bestVariantIndex = i;
    }
    else if (score === bestVariantScore && i < bestVariantIndex) {
      bestVariantIndex = i;
    }
  }

  return slicePropertyVariant(selectedRow, bestVariantIndex);
};

export default resolvePropertySearch;
